import { Injectable } from '@angular/core';
import { ChatMessageProcessor } from './chat-message-processor.service';

/**
 * Service for exposing MessageRegistry metrics via browser console
 * Useful for debugging and monitoring in development/production
 */
@Injectable({
  providedIn: 'root'
})
export class MetricsConsoleService {

  constructor(private chatMessageProcessor: ChatMessageProcessor) {
    this.exposeConsoleCommands();
  }

  private exposeConsoleCommands(): void {
    // Only expose in non-production environments or when explicitly enabled
    if (typeof window !== 'undefined') {
      // Add global functions to window for console access
      (window as any).finderwise = {
        ...(window as any).finderwise,

        /**
         * Get current MessageRegistry metrics
         * Usage: finderwise.getMetrics()
         */
        getMetrics: () => {
          const metrics = this.chatMessageProcessor.getMetrics();
          console.table(metrics);
          return metrics;
        },

        /**
         * Get current MessageRegistry statistics
         * Usage: finderwise.getStats()
         */
        getStats: () => {
          const stats = this.chatMessageProcessor.getRegistryStats();
          console.table(stats);
          return stats;
        },

        /**
         * Get comprehensive report
         * Usage: finderwise.getReport()
         */
        getReport: () => {
          const metrics = this.chatMessageProcessor.getMetrics();
          const stats = this.chatMessageProcessor.getRegistryStats();

          const report = {
            timestamp: new Date().toISOString(),
            summary: {
              totalMessages: metrics.totalMessages,
              activeMessages: stats.activeMessages,
              completedMessages: stats.completedMessages,
              lateChunkRate: `${metrics.lateChunkRate.toFixed(2)}%`,
              duplicatesPrevented: metrics.duplicatesPreventedCount,
              averageLateDelay: `${metrics.averageLateChunkDelay.toFixed(0)}ms`
            },
            metrics,
            stats
          };

          console.group('🎯 FinderWise MessageRegistry Report');
          console.log('📊 Summary:', report.summary);
          console.log('📈 Detailed Metrics:', report.metrics);
          console.log('📉 Registry Stats:', report.stats);
          console.groupEnd();

          return report;
        },

        /**
         * Monitor metrics with periodic updates
         * Usage: finderwise.startMonitoring(5000) // every 5 seconds
         */
        startMonitoring: (intervalMs: number = 10000) => {
          if ((window as any)._metricsInterval) {
            clearInterval((window as any)._metricsInterval);
          }

          console.log(`🔍 Starting metrics monitoring every ${intervalMs}ms`);

          (window as any)._metricsInterval = setInterval(() => {
            const report = (window as any).finderwise.getReport();

            // Highlight issues
            if (report.metrics.lateChunkRate > 5) {
              console.warn(`⚠️ High late chunk rate: ${report.metrics.lateChunkRate.toFixed(2)}%`);
            }

            if (report.metrics.averageLateChunkDelay > 1000) {
              console.warn(`⚠️ High average delay: ${report.metrics.averageLateChunkDelay.toFixed(0)}ms`);
            }
          }, intervalMs);

          return 'Monitoring started. Use finderwise.stopMonitoring() to stop.';
        },

        /**
         * Stop monitoring
         * Usage: finderwise.stopMonitoring()
         */
        stopMonitoring: () => {
          if ((window as any)._metricsInterval) {
            clearInterval((window as any)._metricsInterval);
            (window as any)._metricsInterval = null;
            console.log('🛑 Metrics monitoring stopped');
            return 'Monitoring stopped';
          }
          return 'No monitoring was active';
        },

        /**
         * Log help information
         * Usage: finderwise.help()
         */
        help: () => {
          console.group('🔧 FinderWise Console Commands');
          console.log('finderwise.getMetrics()     - Get late chunk metrics');
          console.log('finderwise.getStats()       - Get registry statistics');
          console.log('finderwise.getReport()      - Get comprehensive report');
          console.log('finderwise.startMonitoring([interval]) - Monitor metrics periodically');
          console.log('finderwise.stopMonitoring() - Stop monitoring');
          console.log('finderwise.help()           - Show this help');
          console.groupEnd();
          return 'Commands available above ☝️';
        }
      };

      // Log initial message
      console.log(
        '%c🎯 FinderWise Metrics Console Loaded',
        'color: #4CAF50; font-weight: bold; font-size: 14px;'
      );
      console.log('Type finderwise.help() for available commands');
    }
  }
}